import {useMemo} from "react";
import {useFormContext} from "react-hook-form";
import {FiCalendar, FiClock, FiRepeat} from "react-icons/fi";

import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {formatDateTime} from "@/lib/format.tsx";
import {MaintenanceScheduleType} from "@/lib/types/maintenance.ts";
import {MaintenanceScheduleFormData} from "@/pages/maintenance/maintenance-create/CreateEditMaintenanceScheduleForm.types.ts";
import {buildRRuleString} from "@/pages/maintenance/maintenance-create/CreateEditMaintenanceScheduleForm.utils.tsx";
import {FrequencyType} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.types.ts";
import {
  formatStartDescription,
  getDurationDescription,
  getNextOccurrenceFromForm,
  getRecurrenceDisplay,
} from "@/pages/maintenance/maintenance.utils.ts";

export const MaintenanceInfoBlock = () => {
  const {watch} = useFormContext<MaintenanceScheduleFormData>();
  const {user} = useCurrentUserContext();
  const timezone = user!.account!.timezone;

  const formData = watch();

  const info = useMemo(() => {
    if (!formData.start_date) return null;

    const title = formatStartDescription(formData, timezone);
    const duration = getDurationDescription(formData);
    const repeat =
      formData.frequency_type === FrequencyType.DONT_REPEAT
        ? null
        : getRecurrenceDisplay({
            schedule_type: MaintenanceScheduleType.RRULE,
            rrule: buildRRuleString(formData, timezone),
          });
    const nextOccurrence = getNextOccurrenceFromForm(formData, timezone);
    const nextText = nextOccurrence ? formatDateTime(nextOccurrence, timezone) : null;

    return {title, duration, repeat, nextText};
  }, [formData, timezone]);

  if (!info) {
    return (
      <Card variant="blue" className="p-3 pulse-color-gray-500">
        <div className="d-flex align-items-center gap-2">
          <Icon icon={FiCalendar} size="normal" />
          <span className="pulseTextBody">Set a start date and frequency to see the schedule summary.</span>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="blue">
      <div className="d-flex align-items-start gap-3">
        <div className="d-flex flex-column gap-3">
          <div className={"pulseTextBodySmallBold"}>{info.title}</div>

          <div className="d-flex flex-wrap gap-3">
            <div className={"d-flex gap-1 align-items-center pulseTextBodySmall"}>
              <Icon icon={FiClock} /> <div>{info.duration}</div>
            </div>

            {info.repeat && (
              <div className={"d-flex gap-1 align-items-center pulseTextBodySmall"}>
                <Icon icon={FiRepeat} />
                <div>{info.repeat}</div>
              </div>
            )}
            {info.nextText && (
              <div className={"d-flex gap-1 align-items-center pulseTextBodySmall"}>
                <Icon icon={FiCalendar} />
                <div>Next: {info.nextText}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
